import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Skeleton } from "../components/ui/skeleton";
import { Plus, Search, MoreHorizontal, Eye, Edit, FileText, Filter, ClipboardList } from "lucide-react";
import TenderDetailsModal from "../components/TenderDetailsModal";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

const StatusBadge = ({ status }) => {
  const config = {
    PENDING: "status-pending",
    APPROVED: "status-approved",
    REJECTED: "status-rejected",
    RETURNED: "status-returned",
  };
  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${config[status] || "status-draft"}`}>
      {status}
    </span>
  );
};

const ProcurementBadge = ({ mode }) => {
  const config = {
    DIRECT_PURCHASE: { label: "Direct", className: "bg-green-50 text-green-700 border-green-200" },
    LOCAL_PURCHASE_COMMITTEE: { label: "LPC", className: "bg-blue-50 text-blue-700 border-blue-200" },
    TENDER_ENQUIRY: { label: "Tender", className: "bg-purple-50 text-purple-700 border-purple-200" },
    OEM_COMPARISON: { label: "OEM Compare", className: "bg-cyan-50 text-cyan-700 border-cyan-200" },
    LPC: { label: "LPC", className: "bg-blue-50 text-blue-700 border-blue-200" },
    PAC_LPC: { label: "PAC/LPC", className: "bg-orange-50 text-orange-700 border-orange-200" },
    TENDER: { label: "Tender", className: "bg-purple-50 text-purple-700 border-purple-200" },
  };
  const c = config[mode] || { label: mode, className: "bg-slate-50 text-slate-700" };
  return (
    <span className={`px-2 py-1 rounded text-xs font-medium border ${c.className}`}>
      {c.label}
    </span>
  );
};

const ChannelBadge = ({ channel }) => {
  if (channel === "GEM") return <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">GeM</span>;
  if (channel === "NON_GEM") return <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800">Non-GeM</span>;
  return null;
};

const PurchaseRequisition = () => {
  const navigate = useNavigate();
  const { user, getAuthHeader } = useAuth();
  const [requisitions, setRequisitions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [tenderModalReq, setTenderModalReq] = useState(null);

  useEffect(() => {
    fetchRequisitions();
  }, []);

  const fetchRequisitions = async () => {
    try {
      const response = await axios.get(`${API_URL}/requisitions`, getAuthHeader());
      setRequisitions(response.data);
    } catch (error) {
      toast.error("Failed to fetch requisitions");
    } finally {
      setLoading(false);
    }
  };

  const filteredRequisitions = requisitions.filter(req =>
    req.requisition_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
    req.item_description.toLowerCase().includes(searchTerm.toLowerCase()) ||
    req.department.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <>
    <div className="space-y-6" data-testid="requisitions-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Purchase Requests</h1>
          <p className="text-slate-500 mt-1">Unified purchase requisitions with tender details</p>
        </div>
        {user?.role === "INDENTER" && (
          <Button 
            onClick={() => navigate("/requisitions/new")}
            className="bg-slate-900 hover:bg-slate-800"
            data-testid="new-requisition-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Purchase Request
          </Button>
        )}
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search requisitions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-requisitions"
              />
            </div>
            <Button variant="outline" className="gap-2">
              <Filter className="w-4 h-4" />
              Filters
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-4">
              {[1, 2, 3, 4, 5].map(i => (
                <div key={i} className="flex items-center gap-4">
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-4 flex-1" />
                  <Skeleton className="h-4 w-20" />
                  <Skeleton className="h-4 w-24" />
                </div>
              ))}
            </div>
          ) : filteredRequisitions.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="font-semibold">Req. Number</TableHead>
                  <TableHead className="font-semibold">Description</TableHead>
                  <TableHead className="font-semibold">Department</TableHead>
                  <TableHead className="font-semibold">Est. Value</TableHead>
                  <TableHead className="font-semibold">Channel</TableHead>
                  <TableHead className="font-semibold">Mode</TableHead>
                  <TableHead className="font-semibold">Status</TableHead>
                  <TableHead className="font-semibold">Date</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredRequisitions.map((req) => (
                  <TableRow 
                    key={req.id} 
                    className="cursor-pointer hover:bg-slate-50"
                    onClick={() => navigate(`/requisitions/${req.id}`)}
                    data-testid={`requisition-row-${req.id}`}
                  >
                    <TableCell className="font-medium text-slate-900">
                      {req.requisition_number}
                    </TableCell>
                    <TableCell className="max-w-xs truncate text-slate-600">
                      {req.item_description}
                    </TableCell>
                    <TableCell className="text-slate-600">{req.department}</TableCell>
                    <TableCell className="font-semibold text-slate-900">
                      ₹{req.estimated_value.toLocaleString('en-IN')}
                    </TableCell>
                    <TableCell>
                      <ChannelBadge channel={req.procurement_channel} />
                    </TableCell>
                    <TableCell>
                      <ProcurementBadge mode={req.procurement_mode} />
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={req.status} />
                    </TableCell>
                    <TableCell className="text-slate-500 text-sm">
                      {new Date(req.created_at).toLocaleDateString('en-IN')}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                          <Button variant="ghost" size="icon">
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={(e) => { e.stopPropagation(); navigate(`/requisitions/${req.id}`); }}>
                            <Eye className="w-4 h-4 mr-2" />
                            View Details
                          </DropdownMenuItem>
                          {!["COMPLETED", "REJECTED", "CANCELLED"].includes(req.status) && (
                            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); navigate(`/requisitions/${req.id}/edit`); }} data-testid={`edit-pr-${req.id}`}>
                              <Edit className="w-4 h-4 mr-2" />
                              Edit Requisition
                            </DropdownMenuItem>
                          )}
                          {["TENDER", "TENDER_ENQUIRY", "LPC", "LOCAL_PURCHASE_COMMITTEE", "PAC_LPC", "OEM_COMPARISON"].includes(req.procurement_mode) && (
                            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); setTenderModalReq(req); }} data-testid={`tender-details-btn-${req.id}`}>
                              <ClipboardList className="w-4 h-4 mr-2" />
                              Tender Details
                            </DropdownMenuItem>
                          )}
                          {req.procurement_mode !== "DIRECT_PURCHASE" && req.status === "APPROVED" && (
                            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); navigate(`/indents/new?requisition=${req.id}`); }}>
                              <FileText className="w-4 h-4 mr-2" />
                              Create Indent
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="p-12 text-center">
              <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-600 font-medium">No requisitions found</p>
              <p className="text-sm text-slate-400 mb-4">
                {searchTerm ? "Try a different search term" : "Create your first purchase requisition"}
              </p>
              {user?.role === "INDENTER" && !searchTerm && (
                <Button onClick={() => navigate("/requisitions/new")} data-testid="empty-state-new-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  New Requisition
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Stats */}
      {!loading && requisitions.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="p-4">
            <p className="text-sm text-slate-500">Total</p>
            <p className="text-2xl font-bold text-slate-900">{requisitions.length}</p>
          </Card>
          <Card className="p-4">
            <p className="text-sm text-slate-500">Pending</p>
            <p className="text-2xl font-bold text-amber-600">
              {requisitions.filter(r => r.status === "PENDING").length}
            </p>
          </Card>
          <Card className="p-4">
            <p className="text-sm text-slate-500">Approved</p>
            <p className="text-2xl font-bold text-emerald-600">
              {requisitions.filter(r => r.status === "APPROVED").length}
            </p>
          </Card>
          <Card className="p-4">
            <p className="text-sm text-slate-500">Total Value</p>
            <p className="text-2xl font-bold text-slate-900">
              ₹{requisitions.reduce((sum, r) => sum + r.estimated_value, 0).toLocaleString('en-IN')}
            </p>
          </Card>
        </div>
      )}
    </div>

    {/* Tender Details Modal */}
    {tenderModalReq && (
      <TenderDetailsModal
        requisition={tenderModalReq}
        onClose={() => setTenderModalReq(null)}
        onSaved={fetchRequisitions}
      />
    )}
    </>
  );
};

export default PurchaseRequisition;
